import { useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Alert from "react-bootstrap/Alert";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Stack from "react-bootstrap/Stack";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPaperPlane,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

import { MainLayout } from "../layouts/mainLayout";
import { ContactFormBindingModel, ValidationProblemDetails } from "../apiClient";
import { useApiClient } from "../helpers/useApiClient";
import { ApiError } from "../components/apiError";
import { RecaptchaDisclaimer } from "../components/recaptchaDisclaimer";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Please enter your name"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Please enter your email address so we can get back to you"),
  message: Yup.string().required("Please enter your message"),
});

export const Contact = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const apiClient = useApiClient();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<Error | ValidationProblemDetails | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      message: "",
    },
    validationSchema,
    onSubmit: async () => {
      setError(null);
      setSuccess(false);
      try {
        await apiClient.contactForm(
          new ContactFormBindingModel({
            name: formik.values.name,
            email: formik.values.email,
            message: formik.values.message,
            captcha: executeRecaptcha ? await executeRecaptcha("contact") : undefined,
          })
        );
        setSuccess(true);
        formik.resetForm();
        setConfirmed(false);
      } catch (err) {
        console.error(err);
        if (err instanceof ValidationProblemDetails || err instanceof Error) {
          setError(err);
        }
      }
    },
  });

  return (
    <MainLayout loading={formik.isSubmitting} title="Contact Us">
      <Container className="py-4" style={{ maxWidth: 740 }}>
        <Link to="/" className="kf-back-link">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to home</span>
        </Link>

        <ApiError error={error} />

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(false)} className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-1">
              <FontAwesomeIcon icon={faCircleCheck} size="lg" />
              <Alert.Heading className="mb-0 fs-5">Thank you for reaching out!</Alert.Heading>
            </div>
            <p className="mb-0 mt-2">
              We have received your message and will get back to you as soon as possible.
            </p>
          </Alert>
        )}

        <div className="kf-form-card">
          <h1 className="h2 mb-2">Contact Us</h1>
          <p className="text-secondary mb-4">
            Have a question, feedback, or a partnership inquiry? We&apos;d love to hear from you.
          </p>

          <Form onSubmit={formik.handleSubmit}>
            <Stack gap={4}>
              <Form.Group controlId="name">
                <Form.Label>
                  Your Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Alex Morgan"
                  name="name"
                  required
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!(formik.touched.name && formik.errors.name)}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="email">
                <Form.Label>
                  Your Email Address <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="e.g. alex@example.com"
                  name="email"
                  required
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!(formik.touched.email && formik.errors.email)}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="message">
                <Form.Label>
                  Message <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Type your message here..."
                  name="message"
                  required
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!(formik.touched.message && formik.errors.message)}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.message}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="p-3 rounded-3" style={{ background: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.08)" }}>
                <Form.Check
                  type="checkbox"
                  id="confirmation"
                  label="I understand that KegFinder is a free community directory indexing external retailers, and does not sell or distribute beer kegs directly."
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
              </div>

              <RecaptchaDisclaimer />

              <div>
                <Button
                  variant="primary"
                  type="submit"
                  size="lg"
                  className="btn-submit d-inline-flex align-items-center gap-2"
                  disabled={formik.isSubmitting || !formik.isValid || !confirmed}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  <span>Send Message</span>
                </Button>
              </div>
            </Stack>
          </Form>
        </div>
      </Container>
    </MainLayout>
  );
};